import { cn } from '@/common/lib/cn/cn'
import React, { forwardRef, isValidElement, type ReactNode } from 'react'
import { Button } from '@/common/ui/Form/Button'
import { Icon, type IconName } from '../../Icon'

export type InputType = {
    size?: 'sm' | 'md' | 'lg' | 'xl'
    emphasis?: 'neutral' | 'negative' | 'positive' | 'warning'
    disabled?: boolean
    beforeIcon?: IconName
    afterIcon?: IconName
    /** Заменяет область `afterIcon` (например кликабельная очистка). */
    afterSlot?: ReactNode
    /** Подсветка как у активного контрола (класс `active` + акцентный бордер). */
    active?: boolean
    className?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>

type InputSize = NonNullable<InputType['size']>

/** Сдвиг кнопки справа от края поля (только для `afterSlot` с `<Button />`). */
const getAfterSlotButtonMarginRight = (fieldSize: InputSize): string => {
    if (fieldSize === 'xl') return '-21px'
    if (fieldSize === 'lg') return '-16px'
    if (fieldSize === 'md') return '-11px'
    return '-6px'
}

export const Input = forwardRef<HTMLInputElement, InputType>(
    (
        {
            size = 'md',
            emphasis = 'neutral',
            beforeIcon,
            afterIcon,
            afterSlot,
            disabled,
            active,
            className,
            type,
            ...props
        },
        ref
    ) => {
        const inputClasses = cn({
            'input-sm': size === 'sm',
            'input-md': size === 'md',
            'input-lg': size === 'lg',
            'input-xl': size === 'xl',
        })

        const labelClasses = cn(
            'input',
            {
                'input-neutral': emphasis === 'neutral',
                'input-negative': emphasis === 'negative',
                'input-positive': emphasis === 'positive',
                'input-warning': emphasis === 'warning',
                'input-disabled': disabled,
            },
            inputClasses,
            active && 'active',
            className
        )

        const renderedAfterSlot = (() => {
            if (!afterSlot) {
                return null
            }

            if (isValidElement(afterSlot) && afterSlot.type === Button) {
                return (
                    <span
                        className='inline-flex shrink-0 items-center'
                        style={{ marginRight: getAfterSlotButtonMarginRight(size) }}
                    >
                        {afterSlot}
                    </span>
                )
            }

            return afterSlot
        })()

        return (
            <label className={labelClasses}>
                {beforeIcon && <Icon name={beforeIcon} className='input-icon' size={size} />}
                <input ref={ref} className={inputClasses} disabled={disabled} type={type} {...props} />
                {renderedAfterSlot
                    ? renderedAfterSlot
                    : afterIcon && <Icon name={afterIcon} className='input-icon' size={size} />}
            </label>
        )
    }
)

Input.displayName = 'Input'
